import React, { useState, useRef, useMemo, useEffect, useCallback, useDeferredValue } from "react";
import { EducationalOrganizationsDTO, EducationalOrganizationsOptimizedDTO, OrganizationType } from "../../types";
import { useCompaniesStore, useFilterStore, useOrganizationsStore } from "../../store";
import { ENTITY_TYPE } from "../../types/marker-types";
import { getEntityTypeByOrganizationType } from "../../utils/organization.utils";

type Completion = {
    id: number;
    entityId: string;
    entityType: ENTITY_TYPE;
    text: string;
    priority: number;
    matchIndex: number;
};

type OrganizationSearchProps = {
    onSubmit: (query: string) => void;
    onSelect?: (
        organization: EducationalOrganizationsOptimizedDTO | null
    ) => void;
    mode: "expanded" | "collapsed";
    onExpand: () => void;
    onCollapse: () => void;
};

type MatchResult = {
    index: number;
    priority: number;
};

const MAX_ITEMS_PER_GROUP = 15;

const getMatchResult = (source: string, query: string): MatchResult | null => {
    const lower = source.toLowerCase();
    const idx = lower.indexOf(query);

    if(idx === -1) return null;

    return {
        index: idx,
        priority: idx === 0 ? 0 : 1,
    };
};

export default function OrganizationSearch({
    onSelect,
    onSubmit,
    mode,
    onExpand,
    onCollapse,
}: OrganizationSearchProps) {
    const [query, setQuery] = useState("");
    const [previewQuery, setPreviewQuery] = useState<string | null>(null);
    const displayQuery = previewQuery ?? query;
    const setResults = useFilterStore(s => s.setResults);

    const {category, setCategory} = useFilterStore(s => s);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    const itemRefs = useRef<Map<number, HTMLLIElement>>(new Map());

    const deferredQuery = useDeferredValue(query);
    const isQueryStale = query !== deferredQuery;

    const organizations = useOrganizationsStore(s => s.organizations);
    const companies = useCompaniesStore(s => s.companies);

    useEffect(() => submitSearch(), [category])

    const { colleges, universities } = useMemo(() => {
        let colleges = [];
        let universities = [];

        for(const org of organizations) {
           if(org.type === OrganizationType.SPO) colleges.push(org);
           else universities.push(org);
        }

        return {
            colleges,
            universities
        };
    }, [organizations]);

    const {
        orgSuggestions,
        orgCompletions,
        compCompletions,
    } = useMemo(() => {
        const q = deferredQuery.trim().toLowerCase();
        if(!q) {
            return {
                orgSuggestions: [],
                matchedOrganizationIds: [],
                matchedCompanyIds: [],
                orgCompletions: [],
                compCompletions: [],
            };
        }

        const matchedOrgs: EducationalOrganizationsOptimizedDTO[] = [];

        const matchedOrgIds = new Set<string>();
        const matchedCompIds = new Set<string>();
        const completionsMap = new Map<string, Completion>();

        let completionId = 0;

        const addCompletion = (
            text: string,
            entityType: ENTITY_TYPE,
            entityId: string,
            res: MatchResult
        ) => {
            const existing: Completion | undefined = completionsMap.get(text.toLowerCase());

            if(
                existing &&
                (
                    existing.priority < res.priority ||
                    (
                        existing.priority === res.priority &&
                        existing.matchIndex <= res.index
                    )
                )
            ) {
                return;
            }

            completionsMap.set(text.toLowerCase(), {
                id: completionId++,
                entityId,
                entityType,
                text,
                priority: res.priority,
                matchIndex: res.index,
            });
        };

        const handleOrgMatch = (org: EducationalOrganizationsOptimizedDTO) => {
            let matched = false;

            const orgRes = getMatchResult(org.shortName, q);
            
            if(orgRes) {
                addCompletion(
                    org.shortName,
                    getEntityTypeByOrganizationType(org.type),
                    org.id,
                    orgRes
                );

                matched = true;
            }

            for(const prof of org.professionsAndSpecializations ?? []) {
                if(prof.name) {
                    const res = getMatchResult(prof.name, q);
                    if(res) {
                        addCompletion(
                            prof.name,
                            getEntityTypeByOrganizationType(org.type),
                            org.id,
                            res
                        );

                        matched = true;
                    }
                }

                for(
                    const place of
                    prof.placesOfWork ?? []
                ) {
                    if(place.name) {
                        const res = getMatchResult(place.name, q);
                        if(res) {
                            addCompletion(
                                place.name,
                                getEntityTypeByOrganizationType(org.type),
                                org.id,
                                res
                            );

                            matched = true;
                        }
                    }
                }
            }

            if(matched) {
                matchedOrgs.push(org);
                matchedOrgIds.add(org.id);
            }
        };

        // organizations

        if(category === ENTITY_TYPE.SPO || !category) {
            for(const org of colleges) {
                handleOrgMatch(org);
            }
        }

        if(category === ENTITY_TYPE.UNIVERSITY || !category) {
            for(const org of universities) {
                handleOrgMatch(org);
            }
        }

        // companies

        if(category === ENTITY_TYPE.COMPANY || !category) {
            for(const comp of companies) {
                let matched = false;
                const compRes = getMatchResult(comp.name, q);
                if(compRes) {
                    addCompletion(
                        comp.name,
                        ENTITY_TYPE.COMPANY,
                        comp.id,
                        compRes
                    );

                    matched = true;
                }

                for(const vac of comp.vacancies) {
                    const res = getMatchResult(vac.name, q);
                    if(res) {
                        addCompletion(
                            vac.name,
                            ENTITY_TYPE.COMPANY,
                            comp.id,
                            res
                        );

                        matched = true;
                    }
                }

                if(matched) {
                    matchedCompIds.add(comp.id);
                }
            }
        }

        const sorted = 
            Array.from(completionsMap.values())
                .sort((a, b) =>
                    a.priority - b.priority ||
                    a.matchIndex - b.matchIndex ||
                    a.text.localeCompare(b.text)
                );

        const orgCompletions =
            sorted
            .filter(c => (c.entityType === ENTITY_TYPE.SPO || c.entityType === ENTITY_TYPE.UNIVERSITY))
            .slice(0, MAX_ITEMS_PER_GROUP);

        const compCompletions =
            sorted
            .filter(c => c.entityType === ENTITY_TYPE.COMPANY)
            .slice(0, MAX_ITEMS_PER_GROUP);

        return {
            orgSuggestions: matchedOrgs,
            matchedOrganizationIds: Array.from(matchedOrgIds),
            matchedCompanyIds: Array.from(matchedCompIds),
            orgCompletions,
            compCompletions,
        };

    }, [
        organizations,
        companies,
        deferredQuery,
        category
    ]);

    const visibleCompletions = useMemo(() => {
        return [
            ...orgCompletions,
            ...compCompletions
        ];
    }, [orgCompletions, compCompletions]);

    const selectedCompletion =
        selectedIndex >= 0
        ? visibleCompletions[selectedIndex]
        : visibleCompletions[0] ?? null;

    useEffect(() => {
        if(mode === "expanded") {
            inputRef.current?.focus();
        } else if(
            mode === "collapsed" &&
            inputRef.current ===
            document.activeElement
        ) {
            inputRef.current?.blur();
        }

    }, [mode]);

    useEffect(() => {
        if(selectedIndex >= 0) {
            itemRefs.current
                .get(selectedIndex)
                ?.scrollIntoView({
                    block: "nearest"
                });
        }
    }, [selectedIndex]);

    const acceptCompletion = useCallback((text: string) => {
        setPreviewQuery(null);
        setQuery(text);
        setSelectedIndex(-1);
        requestAnimationFrame(() => {
            if(!inputRef.current) return;

            inputRef.current.setSelectionRange(
                text.length,
                text.length
            );

            inputRef.current.scrollLeft = inputRef.current.scrollWidth;
        });
    }, []);

    const buildSearchResults = useCallback((value: string) => {
        const q = value.trim().toLowerCase();

        if(!q) {
            return {
                organizationIds: [],
                companyIds: [],
            };
        }

        const matchedOrgIds = new Set<string>();
        const matchedCompIds = new Set<string>();

        const buildOrganization = (organization: EducationalOrganizationsOptimizedDTO) => {
            let matched = false;

            if(organization.shortName?.toLowerCase().includes(q)) {
                matched = true;
            }

            for(const prof of organization.professionsAndSpecializations ?? []) {
                if(prof.name?.toLowerCase().includes(q)) {
                    matched = true;
                }

                for(const place of prof.placesOfWork ?? []) {
                    if(place.name?.toLowerCase().includes(q)) {
                        matched = true;
                    }
                }
            }

            if(matched) {
                matchedOrgIds.add(organization.id);
            }
        };

        if(category === ENTITY_TYPE.SPO || !category) {
            for(const org of colleges) {
                buildOrganization(org);
            }
        }

        if(category === ENTITY_TYPE.UNIVERSITY || !category) {
            for(const org of universities) {
                buildOrganization(org);
            }
        }

        if(category === ENTITY_TYPE.COMPANY || !category) {
            for(const comp of companies) {
                let matched = false;

                if(comp.name.toLowerCase().includes(q)) {
                    matched = true;
                }

                for(const vac of comp.vacancies) {
                    if(
                        vac.name.toLowerCase().includes(q)
                    ) {
                        matched = true;
                    }
                }

                if(matched) {
                    matchedCompIds.add(comp.id);
                }
            }
        }

        return {
            organizationIds: Array.from(matchedOrgIds),
            companyIds: Array.from(matchedCompIds),
        };
    }, [
        organizations,
        companies,
        category
    ]);

    const submitSearch = useCallback((text?: string) => {
        const value = (text ?? query).trim();

        if(value) {
            setResults(buildSearchResults(value));
        } else {
            setResults(null);
        }

        onSelect?.(orgSuggestions[0] ?? null);

        inputRef.current?.blur();

        onSubmit(value);
    }, [
        query,
        buildSearchResults,
        orgSuggestions,
        onSelect,
        onSubmit,
        setResults
    ]);

    const clearQuery =useCallback(() => {
        setQuery("");
        setPreviewQuery(null);
        setSelectedIndex(-1);
        setResults(null);
        onSelect?.(null);
        onSubmit("");
    }, [
        setResults,
        onSelect,
        onSubmit
    ]);

    const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>((e) => {
        switch(e.key) {
            case "Tab":
                e.preventDefault();
                if(selectedCompletion) {
                    acceptCompletion(
                        selectedCompletion.text
                    );
                }

                break;
            case "ArrowRight":
                if(
                    inputRef.current &&
                    inputRef.current.selectionStart !==
                    displayQuery.length
                ) {
                    return;
                }

                e.preventDefault();

                if(selectedCompletion) {
                    acceptCompletion(
                        selectedCompletion.text
                    );
                }

                break;
            case "ArrowDown":
                e.preventDefault();

                setSelectedIndex(prev => {
                    const next = Math.min(prev + 1, visibleCompletions.length - 1);
                    if(next >= 0) {
                        setPreviewQuery(
                            visibleCompletions[next].text
                        );
                    }

                    return next;
                });

                break;
            case "ArrowUp":
                e.preventDefault();

                setSelectedIndex(prev => {
                    const next = Math.max(prev - 1, -1);

                    if(next === -1) {
                        setPreviewQuery(null);
                    } else {
                        setPreviewQuery(
                            visibleCompletions[next].text
                        );
                    }

                    return next;
                });

                break;
            case "Enter":
                e.preventDefault();

                if(selectedCompletion && selectedIndex > -1) {
                    acceptCompletion(selectedCompletion.text);
                    submitSearch(selectedCompletion.text);
                } else {
                    submitSearch();
                }

                break;
            case "Escape":
                e.preventDefault();
                e.stopPropagation();

                if(!query && mode === "expanded") {
                    onSubmit("");
                    setTimeout(onCollapse, 0);

                    return;
                }

                clearQuery();

                break;
            }
        }, [
            selectedCompletion,
            displayQuery,
            visibleCompletions,
            query,
            mode,
            acceptCompletion,
            submitSearch,
            clearQuery,
            onSubmit,
            onCollapse
        ]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPreviewQuery(null);
        setSelectedIndex(-1);
        setQuery(e.target.value);
    }, []);

    const handleFocus = useCallback(() => {
        if(mode === "collapsed") {
            onExpand();
        }
    }, [
        mode,
        onExpand
    ]);

    const handleClearOrSubmit =
        useCallback(() => {
            const hasQuery = query.trim().length > 0;

            if(
                hasQuery &&
                mode === "collapsed"
            ) {
                clearQuery();
            } else {
                submitSearch();
            }
        }, [
            query,
            mode,
            clearQuery,
            submitSearch
        ]);

    const hasQuery = query.trim().length > 0;

    const isCollapsed = mode === "collapsed";

    const showClear = hasQuery && isCollapsed;

    return (
        <div className={`search-bar ${mode}`}>
            <div className={`search-bar__tabs ${mode}`}>
                <div
                    className={`search-bar__tab ${
                        category === null
                            ? "active"
                            : ""
                    }`}
                    onClick={() => {
                        setCategory(null);;
                    }}
                >
                    <span className="tab_text">
                        Все
                    </span>
                </div>
                <div
                    className={`search-bar__tab ${
                        category === ENTITY_TYPE.UNIVERSITY
                            ? "active"
                            : ""
                    }`}
                    onClick={() => {
                        setCategory(
                            ENTITY_TYPE.UNIVERSITY
                        );
                    }}
                >
                    <span className="tab_text">
                        Вузы
                    </span>
                </div>
                <div
                    className={`search-bar__tab ${
                        category === ENTITY_TYPE.SPO
                            ? "active"
                            : ""
                    }`}
                    onClick={() => setCategory(ENTITY_TYPE.SPO)}
                >
                    <span className="tab_text">
                        Колледжи
                    </span>
                </div>
                <div
                    className={`search-bar__tab ${
                        category === ENTITY_TYPE.COMPANY
                            ? "active"
                            : ""
                    }`}
                    onClick={() => setCategory(ENTITY_TYPE.COMPANY)}
                >
                    <span className="tab_text">
                        Компании
                    </span>
                </div>
            </div>
            <div className="search-bar__main">
                <div className="search-top">
                    <div className="search-icon">
                        <ion-icon name="search"></ion-icon>
                    </div>
                    <div className="input-wrapper">
                        {!isQueryStale &&
                            previewQuery === null &&
                            selectedCompletion &&
                            (() => {
                                const completionPart =
                                    selectedCompletion.text.slice(
                                        selectedCompletion.matchIndex +
                                        query.length
                                    );

                                return (
                                    <div
                                        className="ghost"
                                        aria-hidden="true"
                                    >
                                        <span className="typed">
                                            {query}
                                        </span>

                                        <span className="completion">
                                            <span className="completion-text">
                                                {completionPart}
                                            </span>
                                        </span>
                                    </div>
                                );
                            })()}
                        <input
                            ref={inputRef}
                            type="text"
                            value={displayQuery}
                            placeholder="Специальность или место работы"
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            className={`search-input ${
                                query &&
                                visibleCompletions.length === 0
                                    ? "no-completion"
                                    : ""
                            }`}
                            aria-autocomplete="inline"
                        />
                    </div>
                    {(!isCollapsed || hasQuery) && (
                        <button
                            type="button"
                            className="search__btn"
                            onClick={handleClearOrSubmit}
                        >
                            {showClear
                                ? <ion-icon name="close"></ion-icon>
                                : <ion-icon name="chevron-forward"></ion-icon>
                            }
                        </button>
                    )}
                </div>
                {!isQueryStale &&
                    visibleCompletions.length > 0 && (
                        <span className="separator"></span>
                    )}
                <div className={`search__dropdown ${
                    visibleCompletions.length === 0
                        ? "hidden"
                        : ""
                }`}>
                    <ul className="custom-scroll search-bar__list">
                        {orgCompletions.length > 0 && (
                            <>
                                <li className="search-bar__list-title">
                                    <span>
                                        Направления
                                    </span>
                                </li>
                                {orgCompletions.map((c, idx) => (
                                    <CompletionItem
                                        key={c.id}
                                        text={c.text}
                                        isSelected={
                                            idx === selectedIndex
                                        }
                                        onSelect={() => {
                                            acceptCompletion(c.text);
                                            submitSearch(c.text);
                                        }}
                                        setRef={(el) => {
                                            if(el) {
                                                itemRefs.current.set(
                                                    idx,
                                                    el
                                                );
                                            } else {
                                                itemRefs.current.delete(
                                                    idx
                                                );
                                            }

                                        }}
                                    />
                                ))}
                            </>
                        )}

                        {compCompletions.length > 0 && (
                            <>
                                <li className="search-bar__list-title">
                                    <span>
                                        Компании и вакансии
                                    </span>
                                </li>
                                {compCompletions.map((c, idx) => {
                                    const absoluteIndex =
                                        idx +
                                        orgCompletions.length;

                                    return (
                                        <CompletionItem
                                            key={c.id}
                                            text={c.text}
                                            isSelected={
                                                absoluteIndex ===
                                                selectedIndex
                                            }
                                            onSelect={() => {
                                                acceptCompletion(c.text);
                                                submitSearch(c.text);
                                            }}
                                            setRef={(el) => {
                                                if(el) {
                                                    itemRefs.current.set(
                                                        absoluteIndex,
                                                        el
                                                    );
                                                } else {
                                                    itemRefs.current.delete(
                                                        absoluteIndex
                                                    );
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}

const CompletionItem = React.memo(({
    text,
    isSelected,
    onSelect,
    setRef
}: {
    text: string;
    isSelected: boolean;
    onSelect: () => void;
    setRef: (
        el: HTMLLIElement | null
    ) => void;
}) => {

    return (
        <li
            ref={setRef}
            className={`search-bar__list-item ${
                isSelected
                    ? "selected"
                    : ""
            }`}
            tabIndex={0}
            onClick={onSelect}
        >
            <span
                style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                }}
            >
                {text}
            </span>

            <ion-icon
                className="search-bar__list-item-chevron"
                name="chevron-forward"
            ></ion-icon>
        </li>
    );
});